import { useDispatch } from 'react-redux';

// material-ui
import { Button, DialogActions, DialogContent, DialogTitle, Divider, Grid, InputLabel, Stack, TextField, Tooltip } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// third-party
import _ from 'lodash';
import * as Yup from 'yup';
import { Form, FormikProvider, FormikValues, useFormik } from 'formik';

// project imports
import { openSnackbar } from 'store/reducers/snackbar';

// assets
import {
  MerchantCashAdvance,
  MerchantCashAdvanceDocument,
  MerchantCashAdvanceStatusHistoryDocument,
  Transaction,
  TransactionType,
  useDeleteManualTransactionMutation,
  usePaymentMethodsQuery,
  useSaveManualTransactionMutation
} from '../../graphql/types';
import CurrencyFormatter from '../../components/input-formatters/CurrencyFormatter';
import React from 'react';
import { underscoreToSpace } from '../../utils/strings';
import { CloseCircleOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';

// constant
const getInitialValues = (entity: FormikValues | null) => {
  const newEntity = {
    amount: 0,
    type: TransactionType.Collection,
    date: new Date(),
    notes: '',
    paymentMethodId: ''
  };

  if (entity) {
    newEntity.amount = entity.amount;
    newEntity.type = entity.type;
    newEntity.date = entity.depositDate;
    newEntity.notes = entity.notes;
    newEntity.paymentMethodId = entity.paymentMethod?.id;
    return _.merge({}, newEntity, entity);
  }

  return newEntity;
};

export interface Props {
  entity?: any;
  mca: MerchantCashAdvance;
  onCancel: () => void;
  onSave: (transaction: Transaction) => void;
}

const AddManualTransaction = ({ mca, entity, onCancel, onSave }: Props) => {
  const dispatch = useDispatch();
  const [updateManualTransaction] = useSaveManualTransactionMutation();
  const [deleteManualTransaction] = useDeleteManualTransactionMutation();
  const { data: paymentMethodsData } = usePaymentMethodsQuery();
  const paymentMethods = paymentMethodsData?.paymentMethods || [];
  const ValidationSchema = Yup.object().shape({
    amount: Yup.number()
      .required('Manual transaction amount is required')
      .test('is-non-zero', 'Amount must be non-zero', (value) => value !== 0),
    paymentMethodId: Yup.string().required('Payment Method is required'),
    type: Yup.string().required('Type is required')
  });

  const deleteHandler = async () => {
    try {
      await deleteManualTransaction({
        variables: { id: entity.id },
        refetchQueries: [
          { query: MerchantCashAdvanceDocument, variables: { id: mca?.id } },
          { query: MerchantCashAdvanceStatusHistoryDocument, variables: { id: mca?.id } }
        ]
      });
      onSave(entity);
      dispatch(
        openSnackbar({
          open: true,
          message: 'Manual transaction deleted successfully.',
          variant: 'alert',
          alert: {
            color: 'success'
          },
          close: true
        })
      );
      onCancel();
    } catch (error) {
      console.error(error);
    }
  };

  const formik = useFormik({
    initialValues: getInitialValues(entity!),
    validationSchema: ValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const saved = await updateManualTransaction({
          variables: {
            dto: {
              id: entity ? entity.id : null,
              amount: parseFloat(values.amount.toString()),
              date: values.date,
              type: values.type,
              notes: values.notes,
              name: mca.referenceMca,
              mcaId: mca.id,
              paymentMethodId: values.paymentMethodId
            }
          },
          refetchQueries: [
            { query: MerchantCashAdvanceDocument, variables: { id: mca?.id } },
            { query: MerchantCashAdvanceStatusHistoryDocument, variables: { id: mca?.id } }
          ]
        });
        onSave(saved.data?.saveManualTransaction as Transaction);
        if (entity) {
          dispatch(
            openSnackbar({
              open: true,
              message: 'ManualTransaction update successfully.',
              variant: 'alert',
              alert: {
                color: 'success'
              },
              close: true
            })
          );
        } else {
          dispatch(
            openSnackbar({
              open: true,
              message: 'ManualTransaction added successfully.',
              variant: 'alert',
              alert: {
                color: 'success'
              },
              close: true
            })
          );
        }

        setSubmitting(false);
        onCancel();
      } catch (error) {
        console.error(error);
      }
    }
  });

  const { errors, touched, values, handleSubmit, isSubmitting, getFieldProps, handleBlur, setFieldValue, handleChange } = formik;
  return (
    <FormikProvider value={formik}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogTitle>{entity ? 'Edit Manual Transaction' : 'New Manual Transaction'}</DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 2.5 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Stack spacing={2} direction={'row'}>
                  <Stack spacing={1.25} sx={{ width: '50%' }}>
                    <InputLabel required={true} htmlFor="ManualTransaction-entity-amount">
                      Amount
                    </InputLabel>
                    <TextField
                      variant="filled"
                      fullWidth
                      id="ManualTransaction-entity-amount"
                      placeholder="Enter ManualTransaction Amount"
                      {...getFieldProps('amount')}
                      error={Boolean(touched.amount && errors.amount)}
                      helperText={touched.amount && errors.amount}
                      InputProps={{
                        inputComponent: CurrencyFormatter as any
                      }}
                    />
                  </Stack>
                  <Stack spacing={1.25} sx={{ width: '50%' }}>
                    <InputLabel required={true} htmlFor="ManualTransaction-entity-date">
                      Date
                    </InputLabel>
                    <DesktopDatePicker
                      value={values.date ? new Date(values.date) : null}
                      onChange={(value) => setFieldValue('date', value)}
                      slotProps={{
                        textField: {
                          variant: 'filled',
                          error: Boolean(touched.date && errors.date),
                          helperText: touched.date && (errors.date ? <>{errors.date}</> : ''),
                          name: 'date',
                          onBlur: handleBlur
                        }
                      }}
                    />
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={2} direction={'row'}>
                  <Stack spacing={1.25} sx={{ width: '50%' }}>
                    <InputLabel required={true} htmlFor="ManualTransaction-entity-type">
                      Type
                    </InputLabel>
                    <TextField
                      id="ManualTransaction-entity-type"
                      variant="filled"
                      error={Boolean(touched.type && errors.type)}
                      helperText={touched.type && errors.type}
                      fullWidth
                      name="type"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.type}
                      select
                      SelectProps={{ native: true }}
                    >
                      <option value={''}>Select Type</option>
                      {[
                        TransactionType.Collection,
                        TransactionType.Refund,
                        TransactionType.Expense,
                        TransactionType.AchProgramFee,
                        TransactionType.OtherFee,
                        TransactionType.DefaultFee,
                        TransactionType.NsfFee
                      ]
                        .filter(
                          (type) =>
                            ![
                              TransactionType.FundingWallet,
                              TransactionType.Remittance,
                              TransactionType.RemittanceFee,
                              TransactionType.CrmAccessFee,
                              TransactionType.Syndication,
                              TransactionType.Debit,
                              TransactionType.Credit
                            ].includes(type)
                        )
                        .sort((a, b) => underscoreToSpace(a).trim().localeCompare(underscoreToSpace(b).trim(), undefined, { sensitivity: 'base' }))
                        .map((type) => (
                          <option key={type} value={type}>
                            {underscoreToSpace(type).trim()}
                          </option>
                        ))}
                    </TextField>
                  </Stack>
                  <Stack spacing={1.25} sx={{ width: '50%' }}>
                    <InputLabel required={true} htmlFor="ManualTransaction-entity-paymentMethodId">
                      Payment Method
                    </InputLabel>
                    <TextField
                      id="ManualTransaction-entity-paymentMethodId"
                      variant="filled"
                      error={Boolean(touched.paymentMethodId && errors.paymentMethodId)}
                      helperText={touched.paymentMethodId && errors.paymentMethodId}
                      fullWidth
                      name="paymentMethodId"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.paymentMethodId}
                      select
                      SelectProps={{ native: true }}
                    >
                      <option value={''}>Select Payment Method</option>
                      {paymentMethods
                        .filter((method) => !['MANUAL', 'SYNDICATION'].includes(method.id))
                          .sort((a, b) =>
                              underscoreToSpace(a.id).trim().localeCompare(underscoreToSpace(b.id).trim(), undefined, { sensitivity: 'base' })
                          )
                        .map((method) => (
                          <option key={method.id} value={method.id}>
                            {underscoreToSpace(method.id).trim()}
                          </option>
                        ))}
                    </TextField>
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1.25}>
                  <InputLabel htmlFor="ManualTransaction-entity-description">Notes</InputLabel>
                  <TextField
                    variant="filled"
                    fullWidth
                    id="ManualTransaction-entity-description"
                    placeholder="Enter Notes"
                    {...getFieldProps('notes')}
                    error={Boolean(touched.notes && errors.notes)}
                    helperText={touched.notes && errors.notes}
                  />
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Grid container justifyContent="space-between" alignItems="center">
              {/*<Grid item>
                {entity && (
                  <Tooltip title="Delete Transaction" placement="top">
                    <IconButton onClick={deleteHandler} size="large" color="error">
                      <DeleteFilled />
                    </IconButton>
                  </Tooltip>
                )}
              </Grid>*/}
              <Grid item>&nbsp;</Grid>
              <Grid item>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button startIcon={<CloseCircleOutlined />} color="error" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button
                    startIcon={entity ? <SaveOutlined /> : <PlusOutlined />}
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                  >
                    {entity ? 'Save Transaction' : 'Add Transaction'}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </DialogActions>
        </Form>
      </LocalizationProvider>
    </FormikProvider>
  );
};

export default AddManualTransaction;
